import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const GOOGLE_SVG = (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

/**
 * Reusable sign-in form: email/password + Google.
 *
 * Props:
 *   onSuccess          – () => void — called after either method succeeds
 *   compact            – boolean    — tighter spacing for modal contexts
 *   onNavigateToSignUp – () => void — optional "Don't have an account?" handler
 */
export default function AuthForm({ onSuccess, compact = false, onNavigateToSignUp }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, loginWithGoogle } = useAuth();

  const run = async (fn) => {
    setError('');
    setLoading(true);
    try {
      const result = await fn();
      if (result.success) {
        onSuccess?.();
      } else {
        setError(result.message || 'Sign in failed. Please check your details and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    run(() => login({ email, password }));
  };

  const handleGoogle = () => {
    run(() => loginWithGoogle());
  };

  const fs = compact ? 14 : 16;
  const pad = compact ? '10px 12px' : '12px 16px';

  const inputStyle = {
    width: '100%',
    boxSizing: 'border-box',
    marginBottom: compact ? 10 : 14,
    padding: pad,
    borderRadius: 12,
    border: '1.5px solid var(--color-border, #ddd)',
    fontSize: fs,
    background: 'var(--color-bg-card)',
    color: 'var(--color-text)',
    outline: 'none',
    fontFamily: 'inherit',
  };

  return (
    <div>
      {error && (
        <div
          role="alert"
          style={{
            marginBottom: compact ? 10 : 14,
            padding: '9px 12px',
            borderRadius: 10,
            background: 'rgba(220,38,38,0.08)',
            border: '1px solid rgba(220,38,38,0.25)',
            fontSize: 13,
            color: '#dc2626',
            lineHeight: 1.45,
          }}
        >
          {error}
        </div>
      )}

      <form onSubmit={handleEmailSubmit}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          style={inputStyle}
          required
          autoComplete="email"
          disabled={loading}
        />
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          style={{ ...inputStyle, marginBottom: compact ? 12 : 20 }}
          required
          autoComplete="current-password"
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            width: '100%',
            marginBottom: compact ? 8 : 12,
            padding: compact ? '11px 16px' : '13px 16px',
            borderRadius: 12,
            border: 'none',
            background: 'var(--color-primary)',
            color: '#fff',
            fontWeight: 700,
            fontSize: fs,
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.7 : 1,
            fontFamily: 'inherit',
          }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>

      <div
        style={{
          textAlign: 'center',
          margin: compact ? '8px 0' : '12px 0',
          fontSize: 13,
          color: 'var(--color-secondary, var(--color-text-secondary, #888))',
        }}
      >
        or
      </div>

      <button
        type="button"
        onClick={handleGoogle}
        disabled={loading}
        style={{
          width: '100%',
          padding: compact ? '10px 12px' : '12px 16px',
          borderRadius: 12,
          border: '1.5px solid var(--color-border, #ddd)',
          background: 'var(--color-bg-card)',
          color: 'var(--color-text)',
          fontSize: fs,
          fontWeight: 500,
          cursor: loading ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          opacity: loading ? 0.6 : 1,
          fontFamily: 'inherit',
        }}
      >
        {GOOGLE_SVG}
        {loading ? 'Signing in…' : 'Continue with Google'}
      </button>

      {onNavigateToSignUp && (
        <div style={{ textAlign: 'center', marginTop: compact ? 12 : 16 }}>
          <span
            style={{
              fontSize: 13,
              color: 'var(--color-secondary, var(--color-text-secondary, #888))',
            }}
          >
            Don&apos;t have an account?{' '}
          </span>
          <button
            type="button"
            onClick={onNavigateToSignUp}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-primary)',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              padding: 0,
              fontFamily: 'inherit',
            }}
          >
            Create one
          </button>
        </div>
      )}
    </div>
  );
}
